import React, { useState, lazy, Suspense } from 'react';
import { useTranslation } from "react-i18next";
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Card,
    CircularProgress,
    Grid,
    Paper,
    Typography,
} from '@mui/material';
import Label from '../../components/label';
import CustomNoRowsOverlay from './CustomNoRowsOverlay';
import ResidentClothesToolBar from '../../sections/@dashboard/residents/ResidentClothesToolBar';

export default function ResidentClothing({ resident }) {

    const [filterName, setFilterName] = useState('');
    const { t, i18n } = useTranslation();
    const isLocalMode = process.env.REACT_APP_LOCAL_MODE === '1';
    const DataGridProLazy = lazy(() => import('@mui/x-data-grid-pro').then(m => ({ default: m.DataGridPro })));
    const columns = [
        { field: 'barcode', headerName: t('resident-page-barcode') || 'Barcode' },
        { field: 'sizeName', headerName: t('delivery-page-size') },
        { field: 'brandName', headerName: t('delivery-page-brand') },
        { field: 'colorName', headerName: t('delivery-page-color') },
        { field: 'storageType', headerName: t('delivery-page-storage'), valueGetter: (params) => params.rowNode.type === 'group' ? "" : params.row.storageType === 1 ? t('resident-page-fold') : t('resident-page-hanged') },
        { field: 'status', headerName: t('resident-page-status'), renderCell: (params) => params.rowNode.type === 'group' ? "" : params.row.inCleaning ? (<Label color={'primary'}>{t('resident-page-status-processed')}</Label>) : (<Label color={'success'}>{t('resident-page-status-available')}</Label>) },
        { field: 'notes', headerName: t('resident-page-notes') },
    ];

    const getRowId = (row) => {
        return row.barcode;
    }

    const getTreeDataPath = (clothe) => {
        return [clothe.productName, clothe.barcode]
    }

    const handleFilterByName = (event) => {
        setFilterName(event.target.value);
    };

    return (
        <Grid item xs={12}>
            <Card sx={{ padding: 2 }}>
                <Typography variant="h4">
                    {t('resident-page-wardrobe')} {resident?.webClothes.length > 0 ? `(${resident.webClothes.length})` : ''}
                </Typography>

                <ResidentClothesToolBar filterName={filterName} onFilterName={handleFilterByName} />

                {resident ? (
                    isLocalMode ? (
                        <>
                            <Box sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText', mb: 1 }}>
                                <Typography variant="body2">
                                    <strong>Mode développement :</strong> L'affichage en arbre (tree) est masqué en local faute de licence MUI X Pro. 
                                    L'affichage complet sera disponible en production.
                                </Typography>
                            </Box>
                            <DataGrid
                                rows={resident.webClothes.filter(webClothe => {
                                    if (!filterName) return true;
                                    const q = filterName.toLowerCase();
                                    const nameMatch = (webClothe.productName || '').toLowerCase().includes(q);
                                    const barcodeMatch = (webClothe.barcode || '').toString().toLowerCase().includes(q);
                                    return nameMatch || barcodeMatch;
                                }) || []}
                                columns={columns}
                                pageSize={5}
                                getRowId={getRowId}
                                autoHeight
                                hideFooterRowCount
                                disableRowSelectionOnClick
                                loading={resident.webClothes === undefined}
                                components={{
                                    NoRowsOverlay: () => (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            height="100%">
                                            <CustomNoRowsOverlay text={t('resident-page-no-clothes')} />
                                        </Box>
                                    ),
                                }}
                            />
                        </>
                    ) : (
                        <Suspense fallback={<Box p={2}>{t('resident-page-loading') || 'Loading...'}</Box>}>
                            <DataGridProLazy
                                rows={resident.webClothes.filter(webClothe => {
                                    if (!filterName) return true;
                                    const q = filterName.toLowerCase();
                                    const nameMatch = (webClothe.productName || '').toLowerCase().includes(q);
                                    const barcodeMatch = (webClothe.barcode || '').toString().toLowerCase().includes(q);
                                    return nameMatch || barcodeMatch;
                                }) || []}
                                columns={columns}
                                pageSize={5}
                                getRowId={getRowId}
                                autoHeight
                                treeData
                                hideFooterRowCount
                                disableRowSelectionOnClick
                                getTreeDataPath={getTreeDataPath}
                                groupingColDef={{
                                    headerName: t('resident-page-name'),
                                    flex: 1
                                }}
                                loading={resident.webClothes === undefined}
                                components={{
                                    NoRowsOverlay: () => (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            height="100%">
                                            <CustomNoRowsOverlay text={t('resident-page-no-clothes')} />
                                        </Box>
                                    ),
                                }}
                            />
                        </Suspense>
                    )
                ) : (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        height="100%">
                        <CircularProgress />
                    </Box>
                )}
            </Card>
        </Grid>
    );
}
